/* The drawer's filter panel — the 07.09 programator mock's `filterPanel` and everything inside it,
 * value for value.
 *
 * A boxed CARD, unlike `RailFilters`' run of hairline-separated groups: it floats inside the
 * header block over the search row that opened it, so it needs its own edge and its own elevation.
 *
 * The three option shapes are three classes on one container rather than three components: what
 * differs between them is `display` and the option's own box, and every other declaration — the
 * 250px cap, the scroll, the tick, the ink — is shared. See the component's header for why the
 * three exist at all. */

.panel {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-9px);
  padding: var(--space-11px) var(--space-11px) var(--space-3);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);

  /* `AvatarChip`'s one seam — see `.avatar` below. */
  --sz-avatar: 22px;
}

/* ── The four tabs ───────────────────────────────────────────────────────────────────────── */

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5px);
  align-items: center;
}

/* Off: a hairline outline in quiet ink. On: the ink itself, filled — the mock's own inversion, and
 * the same device the rail's filter badge uses. Never colour alone: the fill, the border and the
 * ink all move together, and `aria-selected` says it to everything that is not looking. */
.tab {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-13px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.tab:hover {
  color: var(--text);
}

.tab:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.tabOn,
/* The hover has to be answered again for the SELECTED tab, and it is not belt and braces: a
 * `:hover` selector outranks a plain class, so `.tab:hover`'s `color: var(--text)` was winning over
 * this block's inverted ink and painting the label the same colour as its own fill. The active tab
 * read as a black blob with no word in it for as long as the pointer rested on it, which is exactly
 * where the pointer is after the press that selected it. */
.tabOn:hover {
  color: var(--bg-card);
  background: var(--text);
  border-color: var(--text);
}

/* ── The tab's own search, over `Sprzęt` and `Mięśnie` only ──────────────────────────────── */

.optionSearch {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
  height: 30px;
  padding: var(--space-0) var(--space-10px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.optionSearch:focus-within {
  box-shadow: var(--ring-field);
}

.optionSearchIcon {
  flex: none;
  width: 13px;
  height: 13px;
  fill: none;
  stroke: var(--text-2);
  stroke-width: 2;
  stroke-linecap: round;
}

.optionSearchInput {
  width: 100%;
  min-width: 0;
  font-family: var(--font-body);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text);
  background: transparent;
  border: 0;
}

.optionSearchInput:focus-visible {
  outline: none;
}

/* ── The option area: rows by default, tiles or pills by class ───────────────────────────── */

.options {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);

  /* The mock's 250px cap, and the height axis fluid downward from the reference viewport — the
   * fit-or-scroll rule's own wording. At 1512x1000 and at 1600x1000 the 250px wins and the panel is
   * the mock's exactly. At the declared 1280x620 floor it did not: an open `Sprzęt` tab left the
   * header 527px tall and the result list 42px, which scrolls (so nothing clipped) but is a list
   * nobody can read. 26vh is 161px there, which gives the list back ~90px, and it crosses 250px at
   * 962px of viewport — below every height this app draws its canon at. */
  max-height: min(250px, 26vh);
  overflow-y: auto;
}

.optionsPills {
  flex-flow: row wrap;
  gap: var(--space-6px);
}

.optionsTiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-6px);
}

/* The ROW — the default, and what `Mięśnie` and `Źródło` draw. */
.option {
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  padding: var(--space-2) var(--space-10px);
  text-align: left;
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-11px);
}

.option:hover {
  background: var(--hover-tint);
}

.option:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.optionOn {
  background: var(--bg-inset);
  border-color: var(--text);
}

/* The PILL — `Typ`. No tick: the fill and the full-strength dot are the state, and `aria-pressed`
 * carries it to everything else. */
.optionsPills .option {
  gap: var(--space-7px);
  height: 30px;
  padding: var(--space-0) var(--space-3);
  border-color: var(--border-2);
  border-radius: var(--r-pill);
}

.optionsPills .optionOn {
  background: var(--bg-chip);
  border-color: var(--text);
}

/* The TILE — `Sprzęt`. A 40x40 square over its own word, three to a row. */
.optionsTiles .option {
  flex-direction: column;
  gap: var(--space-7px);
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  padding: var(--space-9px) var(--space-6px) var(--space-2);
  background: var(--bg-inset);
  border-radius: var(--radius-12px);
}

/* A picked tile is a RING and nothing else (owner, 9 September). The tick that used to sit under
 * the label grew the grid row on every press, which is a jump on a surface whose whole job is to be
 * scanned. `--border-thick` so the edge reads as chosen rather than as hovered, and `aria-pressed`
 * carries the state to anyone who cannot see it. */
.optionsTiles .optionOn {
  background: var(--bg-chip);
  border-color: var(--text);
  border-width: var(--border-thick);
}

.tile {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  font-size: var(--fs-exercise);
  font-style: normal;
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-11px);
}

/* The PHOTOGRAPH in the tile. Same 40x40 box as the letter it replaces, so a piece with a picture
 * and a piece without it stand on the same grid line and nothing moves when one 404s.
 *
 * `photoBox` supplies the fit and the neutral ground; this states only the geometry, which is the
 * split that module exists for — a declaration on both sides is settled by chunk order, and that
 * differs between the dev server and the build. */
.tilePhoto {
  object-position: center;
  background: var(--bg-sunken);
}

/* Ticking a tile inverts the LETTER, which is how the mock reads a state off a monogram. A
 * photograph cannot invert — turning it into a silhouette would hide the very thing it is there to
 * show — so a ticked photo tile keeps its picture and the state is carried by the option's own fill,
 * its `--text` border and the tick beside it, which is three channels without touching the image. */
.optionOn .tile:not(.tilePhoto) {
  color: var(--bg-card);
  background: var(--text);
}

.dot {
  display: block;
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: var(--r-circle);
  opacity: 0.5;
}

.optionOn .dot {
  opacity: 1;
}

/* ── The owner's two marks on `Źródło` ───────────────────────────────────────────────────── */

/* The brand mark is a MASK filled with `--text`, so it is the ink of whichever theme it lands in
 * and can never come out invisible on one of the two grounds. Only the size belongs here — the
 * treatment is `brandMark.module.css`'s and a surface must not restate it. */
.brand {
  width: 22px;
  height: 22px;
}

/* The trainer's own disc is sized through `--sz-avatar` on the panel above, which is the ONE
 * number `AvatarChip` lets a surface state — 22px for a 12px row rather than the roster's 32. Its
 * ground, its hairline, its monogram and its photograph branch stay that module's, so the initials
 * fallback is the same control here as it is in the roster.
 *
 * This class therefore carries the disc's PLACE in the row and nothing else, which is the seam
 * `AvatarChip`'s own header states: a surface restating a diameter here would be a declaration on
 * both sides, settled by chunk order. */
.avatar {
  align-self: center;
}

/* ── The option's words ──────────────────────────────────────────────────────────────────── */

.optionBody {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.optionsTiles .optionBody {
  flex: none;
  align-items: center;
  width: 100%;
}

.optionName {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
  white-space: nowrap;
}

.optionsTiles .optionName {
  max-width: 100%;
  overflow: hidden;
  font-size: var(--fs-eyebrow);
  line-height: var(--lh-normal);
  text-align: center;
  text-overflow: ellipsis;
}

.optionHint {
  font-size: var(--fs-badge);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
  white-space: normal;
}

.tick {
  display: grid;
  flex: none;
  place-items: center;
  width: 19px;
  height: 19px;
  font-size: var(--fs-badge);
  font-style: normal;
  font-weight: var(--fw-bold);
  color: var(--bg-card);
  background: var(--text);
  border-radius: var(--r-circle);
}

.empty {
  padding: var(--space-3) var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  text-align: center;
}

/* ── The foot: `Wyczyść` and `Pokaż N` ───────────────────────────────────────────────────── */

.foot {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding-top: var(--space-2px);
}

.footSpacer {
  flex: 1;
}

.clear {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

.clear:hover {
  color: var(--text);
}

.clear:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The one filled control in the panel, and it is INK rather than the accent: the mock fills it
 * with the theme's own opposite (`dark ? '#ffffff' : '#15130f'`), which is exactly `--text` on
 * `--bg-card`. Putting the accent here would spend the scalpel on a control that only closes a
 * panel. No shadow of any kind — a filled button in this app carries none. */
.apply {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  height: 30px;
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--bg-card);
  white-space: nowrap;
  background: var(--text);
  border: 0;
  border-radius: var(--radius-sm);
}

.apply:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.applyCount {
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 17px;
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-badge);
  font-style: normal;
  font-weight: var(--fw-bold);
  background: var(--invert-badge-wash);
  border-radius: var(--r-pill);
  font-variant-numeric: tabular-nums;
}

/* ── `Język`: its heading, in the option row's own word ──────────────────────────────────── */

.languageTitle {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}
